/**
 * 67.61 (1): the jungle's drawn layers on a planet — the canopy closing, the understory, the vines (rendering/jungleLook.ts
 * `junglePieces`, the sheet's) over each jungle stand's trunks, made round the camera as the near-field streamer's client
 * (rendering/nearField.ts): a forest cell made is its jungle stands' pieces laid into a fixed block of slots in four pooled
 * instanced meshes (four draws for the whole field), evicted as the camera leaves. Each piece fades out over the reach's
 * last quarter in the vertex shader (the litter's material) and, 69.76, grows in over `growS` from its making, with the
 * stands' trunks. Deterministic from the stand (a stream of its cell and slot);
 * drawn only: what hides and slows is core/jungleDepth.ts's field over the same discs.
 */
import * as THREE from 'three';
import type { FloraState, PlanetState, Vec3 } from '../types';
import { BIOMES, offsetOnSphere, tangentBasis } from '../core/biomes';
import { FOREST, basisPlanet, forestCell, forestStands, jungleAt, type ForestCandidate, type ForestKeepOut } from '../core/forest';
import type { ForestDensityMode } from '../core/forestDensity';
import { getSurfaceRadius } from '../core/surface';
import { SeededRandom } from '../core/random';
import { NORMAL_TREE_PERSONS } from '../constants/action';
import { JUNGLE_KINDS, jungleGeometry, junglePieces, type JungleKind } from './jungleLook';
import { fadingMaterial } from './groundLitter';
import type { NearFieldClient } from './nearField';

/** The field's dials: its reach (people), the fade's start (× the reach), the grow-in (seconds, the stands' STANDS_FADE.growS), and each kind's slots per forest cell. */
export const JUNGLE_DRAW = { reachPeople: 90, fadeFrom: 0.75, growS: 0.5, slots: { canopy: 96, fern: 160, bush: 96, vine: 64 } as Record<JungleKind, number> };

/** A planet's forest as the stands draw it (TreeStands' entry). */
export interface JungleForest { planetIndex: number; seed: string; n: number; keepOut: ForestKeepOut[]; basis: Float32Array; density: ForestDensityMode; windSeed: string; jungle: number }

const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _u = new THREE.Vector3(), _c = new THREE.Color();
const _y = new THREE.Vector3(0, 1, 0);

export class JungleField {
  readonly meshes: Record<JungleKind, THREE.InstancedMesh>;
  readonly capacity: number;
  private free: number[] = [];
  /** the slots holding a cell: each mesh draws up to the highest (its count), so the cost follows the jungle in reach */
  private used = new Set<number>();
  private readonly focus = { value: new THREE.Vector3() };
  private readonly band = { value: new THREE.Vector2() };
  /** 69.76: the grow-in (the stands' clock, growS) */
  private readonly grow = { value: new THREE.Vector2(0, JUNGLE_DRAW.growS) };
  /** pieces laid by the last makes (a probe's figure) */
  pieces = 0;

  constructor(private readonly scene: THREE.Object3D, private readonly person: number) {
    const reach = JUNGLE_DRAW.reachPeople * person, cell = FOREST.cellPeople * person;
    // (the cells the streamer can hold: within the reach and a cell, and its eviction's grace — two cells of margin)
    this.capacity = Math.ceil((Math.PI * (reach + 2 * cell) ** 2) / (cell * cell) * 1.5) + 4;
    for (let i = this.capacity - 1; i >= 0; i--) this.free.push(i);
    this.band.value.set(reach * JUNGLE_DRAW.fadeFrom, reach);
    this.meshes = {} as Record<JungleKind, THREE.InstancedMesh>;
    const k = person / 1.8;   // (person units → world)
    for (const kind of JUNGLE_KINDS) {
      const geo = jungleGeometry(kind); geo.scale(k, k, k);
      const n = this.capacity * JUNGLE_DRAW.slots[kind];
      geo.setAttribute('aLitterBorn', new THREE.InstancedBufferAttribute(new Float32Array(n).fill(-1e9), 1).setUsage(THREE.DynamicDrawUsage));
      const mesh = new THREE.InstancedMesh(geo, fadingMaterial(this.focus, this.band, this.grow), n);
      mesh.name = `jungle:${kind}`;
      mesh.frustumCulled = false;
      mesh.userData.noBake = true;
      for (let i = 0; i < n; i++) { mesh.setMatrixAt(i, ZERO); mesh.setColorAt(i, _c.set(1, 1, 1)); }
      mesh.count = 0;
      scene.add(mesh);
      this.meshes[kind] = mesh;
    }
  }

  /** The camera's world point: the pieces fade by their distance from it; 69.76: and the stands' clock (seconds), the grow-in's. */
  follow(at: Vec3, clock = this.grow.value.x): void { this.focus.value.set(at.x, at.y, at.z); this.grow.value.x = clock; }

  /**
   * The streamer's client over `forests` (the planets with the jungle on): a cell's jungle stands, their pieces into a block.
   * `view.far` caps the reach as the forest's does.
   */
  client(forests: readonly JungleForest[]): NearFieldClient<number> {
    const by = new Map(forests.map(f => [f.planetIndex, f]));
    return {
      name: 'jungle',
      planets: () => forests.map(f => f.planetIndex),
      cellsPerEdge: pi => by.get(pi)!.n,
      reach: (_pi, view) => Math.min((JUNGLE_DRAW.reachPeople + FOREST.cellPeople) * this.person, view?.far ?? Infinity),
      make: (pi, planet, cell) => this.make(by.get(pi)!, planet, cell),
      evict: (_pi, _cell, slot) => { if (slot !== null) this.clear(slot); },
    };
  }

  private make(f: JungleForest, live: PlanetState, cell: number): number | null {
    const planet = basisPlanet(live, f.basis), person = this.person;
    const stands = forestStands(planet, f.seed, person, f.n, cell, f.keepOut, f.density, f.windSeed, f.jungle).filter(s => jungleAt(planet, f.seed, person, s.dir, s.biome, f.jungle));
    if (!stands.length || !this.free.length) return null;
    const trees = forestCell(planet, f.planetIndex, f.seed, person, f.n, cell, f.keepOut, f.density, f.windSeed, f.jungle);
    const slot = this.free.pop()!;
    const used = { canopy: 0, fern: 0, bush: 0, vine: 0 } as Record<JungleKind, number>;
    for (const s of stands) this.layStand(slot, used, s, trees.filter(t => standOf(t) === s.k), planet, live);
    for (const kind of JUNGLE_KINDS) {
      const mesh = this.meshes[kind], base = slot * JUNGLE_DRAW.slots[kind];
      for (let i = used[kind]; i < JUNGLE_DRAW.slots[kind]; i++) mesh.setMatrixAt(base + i, ZERO);
      const born = mesh.geometry.getAttribute('aLitterBorn') as THREE.InstancedBufferAttribute;
      (born.array as Float32Array).fill(this.grow.value.x, base, base + JUNGLE_DRAW.slots[kind]);   // 69.76: grown in from now
      born.needsUpdate = true;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      this.pieces += used[kind];
    }
    this.used.add(slot); this.recount();
    return slot;
  }

  /** A stand's pieces (junglePieces in the stand's tangent frame, person units) onto the planet's ground, into the slot's block. */
  private layStand(slot: number, used: Record<JungleKind, number>, s: ForestCandidate, trees: FloraState[], planet: PlanetState, live: PlanetState): void {
    const person = this.person, u = 1.8 / person, P = person / planet.radius;   // (u: world → person units)
    const [t1, t2] = tangentBasis(s.dir);
    const r0 = getSurfaceRadius(planet, s.dir), c = s.dir;
    // the trunks in the stand's frame (person units): each tree's offset along the tangents, its height
    const trunks = trees.map(t => {
      const d = t.surfaceDir, along = (d.x - c.x) * t1.x + (d.y - c.y) * t1.y + (d.z - c.z) * t1.z, across = (d.x - c.x) * t2.x + (d.y - c.y) * t2.y + (d.z - c.z) * t2.z;
      return { x: along * r0 * u, z: across * r0 * u, height: NORMAL_TREE_PERSONS * 1.8 * t.scale };
    });
    if (!trunks.length) return;
    const rng = new SeededRandom(`${s.cell}|${s.k}|jungle`);
    const pieces = junglePieces(trunks, BIOMES[s.biome!].radius * 1.8, () => rng.next());
    for (const p of pieces) {
      const k = p.kind, mesh = this.meshes[k];
      if (used[k] >= JUNGLE_DRAW.slots[k]) continue;
      const d = offsetOnSphere(s.dir, t1, t2, p.x / 1.8, p.z / 1.8, P);   // (offsetOnSphere takes people)
      const r = getSurfaceRadius(planet, d) + (p.y / u);
      _u.set(d.x, d.y, d.z);
      _q.setFromUnitVectors(_y, _u);
      _q2.setFromAxisAngle(_y, p.yaw);
      _m.compose(_p.set(live.position.x + d.x * r, live.position.y + d.y * r, live.position.z + d.z * r), _q.multiply(_q2), _s.set(p.sx, p.sy, p.sz));
      const idx = slot * JUNGLE_DRAW.slots[k] + used[k]++;
      mesh.setMatrixAt(idx, _m);
      mesh.setColorAt(idx, _c.set(p.color));
    }
  }

  private clear(slot: number): void {
    for (const kind of JUNGLE_KINDS) {
      const mesh = this.meshes[kind], n = JUNGLE_DRAW.slots[kind];
      for (let i = 0; i < n; i++) mesh.setMatrixAt(slot * n + i, ZERO);
      mesh.instanceMatrix.needsUpdate = true;
    }
    this.free.push(slot);
    this.used.delete(slot); this.recount();
  }

  /** Each mesh's count: up to the highest slot in use (the free list hands out the lowest first). */
  private recount(): void {
    let top = 0;
    for (const s of this.used) top = Math.max(top, s + 1);
    for (const kind of JUNGLE_KINDS) this.meshes[kind].count = top * JUNGLE_DRAW.slots[kind];
  }

  /** Its draws and the triangles drawn now (the slots up to the highest in use) and at full occupancy. */
  cost(): { draws: number; triangles: number; instances: number; trianglesFull: number } {
    let triangles = 0, instances = 0, trianglesFull = 0;
    for (const kind of JUNGLE_KINDS) {
      const m = this.meshes[kind], g = m.geometry, tri = (g.index ? g.index.count : g.attributes.position.count) / 3;
      instances += m.count; triangles += m.count * tri; trianglesFull += this.capacity * JUNGLE_DRAW.slots[kind] * tri;
    }
    return { draws: JUNGLE_KINDS.length, triangles, instances, trianglesFull };
  }

  dispose(): void {
    for (const kind of JUNGLE_KINDS) { const m = this.meshes[kind]; this.scene.remove(m); m.geometry.dispose(); m.dispose(); }
  }
}

/** A forest tree's stand slot (forestTreeId: idBase + cell × perCell + k × 64 + i). */
function standOf(t: FloraState): number { return Math.floor(((t.id - FOREST.idBase) % FOREST.perCell) / 64); }
